import { TaxType, NonTaxableTaxType, TaxKey } from './types';

export const formatTaxKey = (taxType: TaxType, amount: number): TaxKey => {
  const [integer, decimal] = amount.toString().split('.');
  if (decimal) {
    return `${taxType}_${integer}_${decimal}` as TaxKey;
  }
  return `${taxType}_${integer}` as TaxKey;
};

export const formatNonTaxableTaxKey = (
  taxType: TaxType,
  nonTaxableTaxType: NonTaxableTaxType,
  amount: number
): TaxKey => {
  if (amount !== 0) {
    throw new Error(
      `${taxType} tax type can only have amount 0, received: ${amount}`
    );
  }
  return `${taxType}_${nonTaxableTaxType}_0` as TaxKey;
};

export const isValidTaxType = (taxType: string): taxType is TaxType => {
  return Object.values(TaxType).includes(taxType as TaxType);
};

export function isTaxKey(key: any): key is TaxKey {
  if (key.includes('__')) {
    return false;
  }
  const parts = key.split('_');
  if (
    parts.length < 2 ||
    parts.length > 3 ||
    parts.some((part: string) => part === '')
  ) {
    return false;
  }

  const [, ...numberParts] = parts;
  if (!isValidTaxType(parts[0])) {
    return false;
  }

  // Special validation for Exempt, NotSubject, and ReverseCharge
  if (
    [
      NonTaxableTaxType.Exempt,
      NonTaxableTaxType.NotSubject,
      NonTaxableTaxType.ReverseCharge,
    ].includes(numberParts[0] as NonTaxableTaxType)
  ) {
    return numberParts.length === 2 && numberParts[1] === '0';
  }

  const numbers = numberParts.map(Number);
  if (
    numbers.some(isNaN) ||
    numbers.some((num: number) => !Number.isInteger(num)) ||
    numbers.some((num: number) => num < 0)
  ) {
    return false;
  }
  return true;
}

export const getTaxType = (taxKey: TaxKey): TaxType => {
  const [taxType] = taxKey.split('_');
  return taxType as TaxType;
};

export const getTaxRate = (taxKey: TaxKey): number => {
  const parts = taxKey.split('_');
  if (parts.length === 3) {
    return Number(`${parts[1]}.${parts[2]}`);
  }
  return Number(parts[1]);
};

export const getNonTaxableTaxType = (
  taxKey: TaxKey
): NonTaxableTaxType | undefined => {
  const [, nonTaxableTaxType] = taxKey.split('_');
  if (isNonTaxableTaxKey(nonTaxableTaxType)) {
    return nonTaxableTaxType as NonTaxableTaxType;
  }
  return undefined;
};

export function isNonTaxableTaxKey(
  nonTaxableTaxKey: string
): nonTaxableTaxKey is NonTaxableTaxType {
  return [
    NonTaxableTaxType.Exempt,
    NonTaxableTaxType.NotSubject,
    NonTaxableTaxType.ReverseCharge,
  ].includes(nonTaxableTaxKey as NonTaxableTaxType);
}
